import { Client } from "@colyseus/sdk";
import { NETWORK } from "@mucki/shared";
import type { ConnectionState } from "../ui/connectionStatus.js";
import type { ZoneRoom, ZoneState } from "./stateSync.js";

/**
 * In development the client is served by Vite on its own port, so it has to
 * address the game server directly. In production the server serves the built
 * client, so same-origin is correct.
 */
export function serverEndpoint(): string {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  if (import.meta.env.DEV) {
    return `${protocol}//${location.hostname}:${NETWORK.defaultPort}`;
  }
  return `${protocol}//${location.host}`;
}

export class ZoneUnreachableError extends Error {
  constructor(readonly endpoint: string, cause: unknown) {
    super(
      `Could not reach the game server at ${endpoint}. Is it running? Start it with "npm run dev".`,
    );
    this.name = "ZoneUnreachableError";
    this.cause = cause;
  }
}

export async function connectToZone(name: string): Promise<ZoneRoom> {
  const endpoint = serverEndpoint();
  try {
    const client = new Client(endpoint);
    return await client.joinOrCreate<ZoneState>(NETWORK.roomName, { name });
  } catch (error) {
    throw new ZoneUnreachableError(endpoint, error);
  }
}

/**
 * Surfaces the connection lifecycle. The SDK reconnects on its own where the
 * room still exists; this only reports what it is doing, and reports a final
 * disconnect rather than leaving a frozen world on screen.
 */
export function watchConnection(room: ZoneRoom, onState: (state: ConnectionState) => void): void {
  onState("connected");
  room.onDrop(() => onState("reconnecting"));
  room.onReconnect(() => onState("connected"));
  room.onLeave(() => onState("disconnected"));
  room.onError((code, message) => {
    console.warn(`[connection] room error ${code}: ${message ?? "no detail"}`);
  });
}
